import { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Clock, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'

import env from '@utils/env'

const WHATSAPP_NUMBER = env.whatsappNumber;

const COURSES_DATA = {
  "linux-and-networking-essentials": {
    title: "Linux & Networking Essentials",
    category: "Networking",
    level: "Beginner",
    price: 8000,
    duration: "30 hrs",
    pitch: "Master Linux from the ground up and build real networking skills — the two base skills every cloud and DevOps engineer needs on day one.",
    overview: "This course covers Linux and networking in two focused modules. Module 1 takes you from kernel architecture and the filesystem through permissions, authentication, package management, and Bash automation. Module 2 covers networking fundamentals, firewalls and hardening, remote access, and day-to-day system administration. Every concept is taught with 18 years of real industry experience and maps directly to what you will use on the job.",
    whatYouLearn: [
      "Understand Linux history, kernel architecture, and why Linux powers the cloud",
      "Navigate the filesystem hierarchy and manage files, directories, and text editors",
      "Control file permissions, ownership, and groups using chmod, chown, and chgrp",
      "Manage users and groups including authentication, SAML, SSO, and OpenLDAP",
      "Configure networking, DNS, iptables, and firewalld on Linux servers",
      "Write Bash shell scripts with variables, conditions, loops, and automation",
      "Set up and harden SSH with key-based authentication and security best practices",
      "Administer Linux systems — services, updates, backups, and monitoring",
      "Manage software packages using APT on Debian and YUM/DNF on RHEL systems",
      "Secure Linux systems with access control, user permissions, and firewall rules",
      "Understand the OSI model and TCP/IP stack from a real-world Linux perspective",
      "Configure and troubleshoot network interfaces, routes, and gateways on Linux",
      "Work with IP addressing, subnetting, and CIDR notation confidently",
      "Use DNS tools like dig, nslookup, and /etc/hosts for name resolution",
    ],
    curriculum: [
      {
        module: 1,
        title: "Linux Fundamentals",
        lessons: [
          "Linux History & the Open Source Ecosystem",
          "Linux Kernel Architecture & Distributions Overview",
          "The Filesystem Hierarchy Standard (FHS)",
          "Navigating the CLI",
          "File and Directory Management",
          "Text Editors",
          "File Permissions & Ownership",
          "Understanding Users, Groups, and the /etc/passwd file",
          "User Management",
          "Group Management and Sudo Privileges",
          "Authentication Deep Dive",
          "Package Management on Debian",
          "Package Management on RHEL",
          "Process Management",
          "Bash Scripting Fundamentals",
          "Writing Automation Scripts with Bash",
        ],
      },
      {
        module: 2,
        title: "Networking and Administration",
        lessons: [
          "Networking Fundamentals",
          "IP Addressing, Subnetting, and CIDR Notation",
          "DNS",
          "Network Interface Configuration on Linux",
          "Routing Tables, Gateways, and Static Routes",
          "iptables",
          "firewalld",
          "SSH Setup",
          "Remote Access",
          "Linux Security Hardening",
          "Access Control",
          "System Services",
          "System Updates, Patching, and Maintenance Workflows",
          "Backups and Recovery",
          "System Monitoring",
          "Log Management",
        ],
      },
    ],
    whoIsThisFor: [
      "Freshers looking to enter cloud or DevOps roles",
      "IT students wanting hands-on Linux skills",
      "Windows admins transitioning to Linux environments",
      "Anyone preparing for AWS, GCP, or Azure certifications",
      "Developers who want to confidently work on Linux servers",
    ],
    tools: ["Linux", "Bash", "SSH", "Vim", "Cron", "Systemd", "iptables", "OpenLDAP"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: [
        "AWS Certified Solutions Architect – Professional",
        "Google Cloud Certified Solutions Architect",
        "RHCE",
        "MCSA",
      ],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Has trained professionals at Cognizant, Mphasis, California Institute of Technology, and Don Bosco Institute of Technology. Brings real project experience to every session.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "cloud-essentials": {
    title: "Cloud Essentials",
    category: "Cloud",
    level: "Beginner",
    price: 8000,
    duration: "20 hrs",
    pitch: "Get cloud-ready across AWS, GCP, and Azure — one course, three platforms, zero fluff.",
    overview: "The perfect entry point into cloud computing. This course introduces the core concepts shared across all major cloud platforms — compute, storage, networking, IAM, and billing — so you can confidently navigate AWS, GCP, and Azure. Built for both students and working professionals.",
    whatYouLearn: [
      "Understand what cloud computing is, how it works, and why it replaced traditional infrastructure",
      "Compare public, private, and hybrid cloud models and when to use each",
      "Navigate the AWS console and understand its core service categories",
      "Provision and manage virtual machines using AWS EC2",
      "Understand cloud storage types — object, block, and file — and when to use each",
      "Configure cloud networking — VPCs, subnets, route tables, and security groups",
      "Manage identity and access with AWS IAM users, roles, policies, and best practices",
      "Understand AWS billing, pricing models, and how to control costs from day one",
      "Use the AWS CLI to automate and manage resources from the command line",
      "Understand the shared responsibility model and AWS security fundamentals",
      "Monitor cloud resources using CloudWatch and AWS native monitoring tools",
      "Understand high availability, fault tolerance, and AWS architecture best practices",
    ],
    curriculum: [
      {
        module: 1,
        title: "Cloud and AWS Foundation",
        lessons: [
          "What is Cloud Computing",
          "Cloud Service Models",
          "Cloud Deployment Models",
          "AWS Overview",
          "The AWS Console",
          "AWS Pricing Models",
          "AWS Billing and Cost Management",
          "The Shared Responsibility Model",
          "AWS Security Fundamentals",
          "AWS Support Plans and Documentation Overview",
        ],
      },
      {
        module: 2,
        title: "Advanced Compute and Storage",
        lessons: [
          "AWS EC2",
          "Launching and Connecting to EC2 Instances",
          "EC2 Storage",
          "AWS S3",
          "AWS EFS",
          "Cloud Networking on AWS",
          "Security Groups and Network ACLs",
          "AWS IAM",
          "IAM Best Practices",
          "AWS CLI",
          "AWS CloudWatch",
          "Elastic Load Balancing and Auto Scaling",
          "AWS RDS",
          "AWS Lambda",
        ],
      },
    ],
    whoIsThisFor: [
      "Complete beginners to cloud computing",
      "Linux/networking students ready to move to cloud",
      "Professionals exploring a cloud career switch",
      "Students targeting AWS/GCP/Azure associate certifications",
    ],
    tools: ["AWS", "GCP", "Azure", "AWS CLI", "GCloud CLI", "Azure CLI"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: ["AWS Certified Solutions Architect – Professional", "Google Cloud Certified Solutions Architect", "RHCE", "MCSA"],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Has trained professionals at Cognizant, Mphasis, California Institute of Technology, and Don Bosco Institute of Technology.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "aws-professional-architect": {
    title: "AWS Professional Architect",
    category: "Cloud",
    level: "Advanced",
    price: 15000,
    duration: "60 hrs",
    pitch: "Design and deploy production-grade systems on AWS — aligned to the AWS Solutions Architect Professional exam.",
    overview: "A complete path from Linux and networking fundamentals to advanced AWS architecture — covering compute, storage, networking, databases, containers, serverless, automation, and cost optimization. Every topic is taught with real-world project scenarios drawn from 18 years of cloud architecture experience. Prepares you for both the job and the AWS SAP-C02 certification.",
    whatYouLearn: [
      "Work confidently on Linux servers — permissions, packages, processes, and Bash automation",
      "Configure and troubleshoot networking on Linux — IP addressing, subnetting, DNS, routing, and firewalls",
      "Understand cloud service and deployment models and how AWS global infrastructure is organised",
      "Architect multi-tier, highly available applications on AWS",
      "Select the right compute and storage services — EC2, EBS, S3, EFS, FSx, and Storage Gateway",
      "Build elastic architectures with launch templates, Auto Scaling, and load balancing",
      "Configure advanced VPC networking — Transit Gateway, VPC Peering, PrivateLink, and Direct Connect",
      "Secure workloads end to end using IAM, WAF, Shield, GuardDuty, and KMS",
      "Choose and architect the right data layer — RDS Multi-AZ, Aurora, DynamoDB, ElastiCache, and Redshift",
      "Build analytics pipelines on S3 using Glue and Athena",
      "Deploy containerized and serverless workloads using ECS, EKS, Lambda, API Gateway, and Step Functions",
      "Implement CI/CD pipelines with CodePipeline, CodeBuild, and CodeDeploy",
      "Define infrastructure as code using CloudFormation and the AWS CDK",
      "Optimize costs using Reserved Instances, Savings Plans, rightsizing, and Cost Explorer",
      "Apply AWS Well-Architected Framework principles across all pillars",
      "Prepare for the SAP-C02 exam with full-length architecture scenarios and exam strategy",
    ],
    curriculum: [
      {
        module: 1,
        title: "Linux and Networking Essentials",
        lessons: [
          "Linux Foundations",
          "Command Line Basics",
          "Permissions & Users",
          "Authentication",
          "Package Management",
          "Processes & Scripting",
          "Networking Fundamentals",
          "Firewalls & Security",
          "Remote Access",
          "System Administration",
        ],
      },
      {
        module: 2,
        title: "Cloud Essentials",
        lessons: [
          "What is Cloud Computing",
          "Cloud Service Models",
          "Cloud Deployment Models",
          "AWS Overview",
          "The AWS Console",
          "AWS Pricing Models",
          "AWS Billing and Cost Management",
        ],
      },
      {
        module: 3,
        title: "Advanced Compute and Storage",
        lessons: [
          "EC2 Fundamentals",
          "EC2 Pricing Options",
          "Storage with EBS",
          "Amazon S3",
          "Elastic File System (EFS)",
          "Amazon FSx & Storage Gateway",
          "Security Groups & NACLs",
          "Networking Basics",
          "Launch Templates & Auto Scaling",
          "Load Balancing",
        ],
      },
      {
        module: 4,
        title: "Networking & Security",
        lessons: [
          "Advanced VPC",
          "Transit Gateway",
          "Direct Connect",
          "WAF",
          "Shield",
          "GuardDuty",
          "KMS",
        ],
      },
      {
        module: 5,
        title: "Databases & Analytics",
        lessons: [
          "RDS Multi-AZ",
          "Aurora",
          "DynamoDB",
          "ElastiCache",
          "Redshift",
          "Glue",
          "Athena",
        ],
      },
      {
        module: 6,
        title: "Containers & Serverless",
        lessons: [
          "ECS",
          "EKS",
          "Lambda",
          "API Gateway",
          "Step Functions",
          "EventBridge",
        ],
      },
      {
        module: 7,
        title: "CI/CD & Infrastructure as Code",
        lessons: [
          "CodePipeline",
          "CodeBuild",
          "CodeDeploy",
          "CloudFormation",
          "CDK",
        ],
      },
      {
        module: 8,
        title: "Cost Optimization & Exam Prep",
        lessons: [
          "Cost Explorer",
          "Trusted Advisor",
          "Well-Architected Tool",
          "Mock Scenarios",
          "Exam Strategy",
        ],
      },
    ],
    whoIsThisFor: [
      "Cloud engineers with 1+ year AWS experience",
      "Solutions architects targeting the SAP-C02 certification",
      "DevOps engineers expanding into cloud architecture",
      "Team leads responsible for AWS infrastructure decisions",
    ],
    tools: ["AWS", "CloudFormation", "Terraform", "AWS CLI", "ECS", "EKS", "Lambda"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: ["AWS Certified Solutions Architect – Professional", "AWS Certified Solutions Architect – Associate", "Google Cloud Certified Solutions Architect", "RHCE"],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Designed and implemented scalable cloud solutions across AWS, Azure, and GCP for enterprise clients. Conducted 6-week boot camps for Cognizant and Mphasis.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "gcp-professional-architect": {
    title: "GCP Professional Architect",
    category: "Cloud",
    level: "Advanced",
    price: 15000,
    duration: "60 hrs",
    pitch: "Master Google Cloud architecture and prepare for the GCP Professional Cloud Architect certification.",
    overview: "A complete path from Linux and networking fundamentals to advanced Google Cloud architecture — covering compute, storage, networking, containers, data engineering, security, and hybrid connectivity. Real-world architecture scenarios throughout, drawn from 18 years of cloud experience. Aligns with the GCP Professional Cloud Architect exam objectives.",
    whatYouLearn: [
      "Work confidently on Linux servers — permissions, packages, processes, and Bash automation",
      "Configure and troubleshoot networking on Linux — IP addressing, subnetting, DNS, routing, and firewalls",
      "Understand cloud service and deployment models and how GCP global infrastructure is organised",
      "Design scalable and reliable systems on GCP",
      "Provision and scale compute with Compute Engine, managed instance groups, and autoscaling",
      "Structure organisations, folders, and projects using the GCP resource hierarchy",
      "Configure VPC networking, Cloud DNS, Cloud NAT, and Cloud Load Balancing",
      "Deploy and manage GKE clusters, Cloud Run, App Engine, and Cloud Functions",
      "Automate container builds and releases with Cloud Build and Artifact Registry",
      "Architect data pipelines using BigQuery, Dataflow, and Pub/Sub",
      "Choose the right data store — Cloud SQL, AlloyDB, Cloud Spanner, Bigtable, and Filestore",
      "Implement security controls using Cloud Armor, Security Command Center, KMS, and IAP",
      "Manage secrets, conditional IAM policies, audit logging, and VPC Service Controls",
      "Connect on-premises networks using Cloud Interconnect, Cloud VPN, and Cloud Router with BGP",
      "Plan migrations and disaster recovery using Migration Center and proven DR patterns",
      "Apply Google Cloud Architecture Framework principles and prepare for the certification exam",
    ],
    curriculum: [
      {
        module: 1,
        title: "Linux and Networking Essentials",
        lessons: [
          "Linux Foundations",
          "Command Line Basics",
          "Permissions & Users",
          "Authentication",
          "Package Management",
          "Processes & Scripting",
          "Networking Fundamentals",
          "Firewalls & Security",
          "Remote Access",
          "System Administration",
        ],
      },
      {
        module: 2,
        title: "Cloud Essentials",
        lessons: [
          "What is Cloud Computing",
          "Cloud Service Models",
          "Cloud Deployment Models",
          "GCP Overview",
          "The GCP Console",
          "GCP Pricing Models",
          "GCP Billing and Cost Management",
        ],
      },
      {
        module: 3,
        title: "GCP Core Infrastructure",
        lessons: [
          "Compute Engine",
          "Cloud Storage",
          "VPC",
          "Cloud DNS",
          "Cloud Load Balancing",
          "IAM",
          "Cloud NAT",
          "Managed Instance Groups & Autoscaling",
          "Persistent Disks & Snapshots",
          "Resource Hierarchy",
        ],
      },
      {
        module: 4,
        title: "Container and App Platforms",
        lessons: [
          "GKE",
          "Cloud Run",
          "App Engine",
          "Artifact Registry",
          "Cloud Build",
          "Cloud Functions",
          "GKE Autoscaling",
        ],
      },
      {
        module: 5,
        title: "Data and Analytics",
        lessons: [
          "BigQuery",
          "Dataflow",
          "Pub/Sub",
          "Cloud Spanner",
          "Bigtable",
          "Looker Studio",
          "Cloud SQL",
          "AlloyDB & Filestore",
        ],
      },
      {
        module: 6,
        title: "Security and Compliance",
        lessons: [
          "Cloud Armor",
          "Security Command Center",
          "KMS",
          "VPC Service Controls",
          "Audit Logging",
          "Conditional IAM Policies",
          "Secret Manager",
          "Identity-Aware Proxy (IAP)",
        ],
      },
      {
        module: 7,
        title: "Networking and Hybrid Cloud",
        lessons: [
          "Cloud Interconnect",
          "Cloud VPN",
          "Shared VPC",
          "Network Intelligence Center",
          "VPC Peering",
          "Cloud Router & BGP",
        ],
      },
      {
        module: 8,
        title: "Architecture Patterns and Exam Prep",
        lessons: [
          "Well-Architected Scenarios",
          "Case Studies",
          "Migration Strategies",
          "Exam Walkthrough",
          "Migration Center & Disaster Recovery",
          "Vertex AI Overview",
        ],
      },
    ],
    whoIsThisFor: [
      "Cloud engineers with GCP or multi-cloud exposure",
      "Architects targeting the GCP Professional Cloud Architect cert",
      "AWS architects expanding to Google Cloud",
      "Data engineers working with GCP data services",
    ],
    tools: ["GCP", "GKE", "BigQuery", "Terraform", "GCloud CLI", "Cloud Build", "Pub/Sub"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: ["Google Cloud Certified Solutions Architect", "AWS Certified Solutions Architect – Professional", "RHCE"],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Holds Google Cloud Certified Solutions Architect credential. Has architected GCP solutions for enterprise clients and trained professionals across India.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "azure-architect": {
    title: "Azure Architect",
    category: "Cloud",
    level: "Advanced",
    price: 15000,
    duration: "60 hrs",
    pitch: "Design enterprise-grade solutions on Microsoft Azure — aligned to the AZ-305 Azure Solutions Architect Expert exam.",
    overview: "A complete path from Linux and networking fundamentals to advanced Azure architecture — covering compute, storage, identity, networking, data services, containers, and governance. Taught with real enterprise architecture scenarios drawn from 18 years of cloud experience. Prepares you for the Azure Solutions Architect Expert (AZ-305) certification and real-world Azure roles.",
    whatYouLearn: [
      "Work confidently on Linux servers — permissions, packages, processes, and Bash automation",
      "Configure and troubleshoot networking on Linux — IP addressing, subnetting, DNS, routing, and firewalls",
      "Understand cloud service and deployment models and how Azure global infrastructure is organised",
      "Navigate the Azure Portal and control spend with Azure pricing models and cost management",
      "Design Azure compute solutions using VMs, VM Scale Sets, App Service, and Azure Functions",
      "Architect Azure storage solutions including Blob Storage, managed disks, and Azure Files",
      "Implement Microsoft Entra ID (Azure AD), RBAC, and Privileged Identity Management",
      "Secure workloads with Key Vault, Defender for Cloud, and Microsoft Sentinel",
      "Design Azure networking with VNets, NSGs, Azure Firewall, and Application Gateway",
      "Build hybrid connectivity using ExpressRoute and Virtual WAN",
      "Choose the right data service — Azure SQL, Cosmos DB, and Azure Cache for Redis",
      "Architect event-driven integration with Service Bus, Event Hub, and Logic Apps",
      "Deploy and operate containers on AKS with Azure Container Registry",
      "Automate delivery using Azure DevOps Pipelines and GitHub Actions on Azure",
      "Apply Azure governance and cost control using Azure Policy and Cost Management",
      "Run a Well-Architected Review and prepare for AZ-305 with full mock scenarios",
    ],
    curriculum: [
      {
        module: 1,
        title: "Linux and Networking Essentials",
        lessons: [
          "Linux Foundations",
          "Command Line Basics",
          "Permissions & Users",
          "Authentication",
          "Package Management",
          "Processes & Scripting",
          "Networking Fundamentals",
          "Firewalls & Security",
          "Remote Access",
          "System Administration",
        ],
      },
      {
        module: 2,
        title: "Cloud Essentials",
        lessons: [
          "What is Cloud Computing",
          "Cloud Service Models",
          "Cloud Deployment Models",
          "Azure Overview",
          "The Azure Portal",
          "Azure Pricing Models",
          "Azure Billing and Cost Management",
        ],
      },
      {
        module: 3,
        title: "Compute and Storage",
        lessons: [
          "VMs",
          "VMSS",
          "App Service",
          "Azure Functions",
          "Blob Storage",
          "Managed Disks",
          "Azure Files",
        ],
      },
      {
        module: 4,
        title: "Identity, Governance & Security",
        lessons: [
          "Azure AD",
          "RBAC",
          "PIM",
          "Key Vault",
          "Defender for Cloud",
          "Microsoft Sentinel",
        ],
      },
      {
        module: 5,
        title: "Networking",
        lessons: [
          "VNet",
          "NSG",
          "Azure Firewall",
          "Load Balancer",
          "Application Gateway",
          "ExpressRoute",
          "Virtual WAN",
        ],
      },
      {
        module: 6,
        title: "Data & Integration Services",
        lessons: [
          "Azure SQL",
          "Cosmos DB",
          "Azure Cache for Redis",
          "Service Bus",
          "Event Hub",
          "Logic Apps",
        ],
      },
      {
        module: 7,
        title: "Containers & CI/CD",
        lessons: [
          "AKS",
          "Azure Container Registry",
          "Azure DevOps Pipelines",
          "GitHub Actions on Azure",
        ],
      },
      {
        module: 8,
        title: "Governance, Cost & Exam Prep",
        lessons: [
          "Azure Policy",
          "Cost Management",
          "Well-Architected Review",
          "AZ-305 Mock Scenarios",
        ],
      },
    ],
    whoIsThisFor: [
      "Azure engineers targeting the AZ-305 certification",
      "Solutions architects working in Microsoft-heavy enterprises",
      "DevOps engineers expanding into Azure architecture",
      "AWS/GCP architects adding Azure to their skillset",
    ],
    tools: ["Azure", "AKS", "Azure DevOps", "Terraform", "Bicep", "Azure CLI", "ARM Templates"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: ["MCSA", "AWS Certified Solutions Architect – Professional", "Google Cloud Certified Solutions Architect", "RHCE"],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Architected Azure Databricks, Unity Catalog, and Azure SQL Synapse solutions for enterprise clients. Deep hands-on Azure experience spanning identity, data, and infrastructure.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "devops-essentials": {
    title: "DevOps Essentials",
    category: "DevOps",
    level: "Intermediate",
    price: 10000,
    duration: "30 hrs",
    pitch: "From zero to job-ready — learn the core tools and practices every DevOps engineer uses on day one.",
    overview: "This course takes you through the complete DevOps toolchain: Git, Docker, CI/CD with Jenkins and GitHub Actions, Kubernetes, configuration management with Ansible, infrastructure as code with Terraform, and monitoring with Prometheus and Grafana. Built for beginners and career-switchers who want to go from zero to confident with real-world tools — not theory. Every module is hands-on, every concept maps directly to what engineering teams use today.",
    whatYouLearn: [
      "Track and collaborate on code using Git — branching, merging, rebasing, and hooks",
      "Package and run applications anywhere using Docker containers and Docker Compose",
      "Build production-ready images with multi-stage builds and push them to registries",
      "Automate builds, tests, and deployments with Jenkins pipelines defined as code",
      "Secure your pipelines with credential scopes, RBAC, and automated versioning",
      "Deploy and manage containerised applications on Kubernetes",
      "Manage application configuration and secrets using ConfigMaps and Kubernetes Secrets",
      "Expose workloads reliably with Kubernetes Services, DNS, and Ingress",
      "Automate server configuration with Ansible playbooks, roles, and Ansible Vault",
      "Provision infrastructure as code with Terraform — state, modules, and reusable configurations",
      "Build event-driven CI/CD workflows using GitHub Actions with OIDC and AWS integration",
      "Run self-hosted GitHub Actions runners and share logic with reusable workflows",
      "Monitor system health with Prometheus and query metrics using PromQL",
      "Route actionable alerts with Alertmanager and visualise metrics in Grafana dashboards",
    ],
    curriculum: [
      {
        module: 1,
        title: "Git & Version Control",
        lessons: [
          "Version Control Basics",
          "Core Git Workflow",
          "Branching & Merging",
          "Undoing Changes",
          "SSH Keys for GitHub",
          "Advanced Git",
          "Git Hooks & Automation",
          "Git Best Practices",
        ],
      },
      {
        module: 2,
        title: "Docker & Containers",
        lessons: [
          "Container Fundamentals",
          "Core Docker Commands",
          "Writing Dockerfiles",
          "Production-Ready Images",
          "Image Registries",
          "Docker Compose",
          "Docker Networking",
          "CI/CD Integration with Docker",
        ],
      },
      {
        module: 3,
        title: "CI/CD with Jenkins",
        lessons: [
          "CI/CD Fundamentals",
          "Jenkins Architecture & Setup",
          "Your First Pipeline",
          "Pipeline as Code",
          "Advanced Pipelines",
          "Integrations",
          "Credentials & Security",
          "Monitoring & Optimization",
          "Versioning",
        ],
      },
      {
        module: 4,
        title: "Kubernetes",
        lessons: [
          "Why Kubernetes",
          "YAML & Declarative Basics",
          "Pods",
          "Scheduling & Organization",
          "Deployments",
          "Configuration & Secrets",
          "Services & Networking",
        ],
      },
      {
        module: 5,
        title: "Ansible",
        lessons: [
          "Ansible Fundamentals",
          "Playbooks",
          "Variables & Secrets",
          "Roles & Collections",
        ],
      },
      {
        module: 6,
        title: "Terraform",
        lessons: [
          "Terraform Fundamentals",
          "Variables & Expressions",
          "State Management",
          "Modules",
          "Provisioners",
        ],
      },
      {
        module: 7,
        title: "CI/CD with GitHub Actions",
        lessons: [
          "GitHub Actions vs Jenkins",
          "Core Concepts",
          "Building CI Pipelines",
          "Secrets & Secure Auth",
          "Reusable Workflows",
          "Self-Hosted Runners",
        ],
      },
      {
        module: 8,
        title: "Monitoring with Prometheus & Grafana",
        lessons: [
          "Monitoring Fundamentals",
          "Prometheus Architecture",
          "Setting Up Prometheus",
          "PromQL",
          "Exporters & Metrics",
          "Alertmanager",
          "Grafana",
        ],
      },
    ],
    whoIsThisFor: [
      "Developers who want to own the full delivery lifecycle",
      "IT professionals transitioning into DevOps or cloud roles",
      "Linux and networking students ready to move into automation",
      "Anyone preparing for Docker, Kubernetes, or Jenkins certifications",
    ],
    prerequisite: {
      text: "This course starts at Git and assumes you are comfortable on the Linux command line. If you are new to Linux, shell scripting, or networking, take",
      title: "Linux & Networking Essentials",
      slug: "linux-and-networking-essentials",
    },
    tools: ["Git", "Docker", "Jenkins", "Kubernetes", "Ansible", "Terraform", "GitHub Actions", "Prometheus", "Grafana"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: ["AWS Certified Solutions Architect – Professional", "Google Cloud Certified Solutions Architect", "RHCE", "MCSA"],
      bio: "Founder of DeOrigin Labs. Led DevOps transformations for enterprise clients, improving deployment efficiency by 40%. Conducted 6-week DevOps boot camps for Cognizant and Mphasis.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
  "kubernetes-architect": {
    title: "Kubernetes Architect",
    category: "DevOps",
    level: "Advanced",
    price: 20000,
    duration: "40 hrs",
    pitch: "Master Kubernetes from the ground up — design, deploy, secure, and operate production-grade container orchestration at enterprise scale.",
    overview: "This course takes experienced engineers deep into Kubernetes architecture and operations. From bootstrapping a cluster the hard way to mastering RBAC, network policies, service mesh with Istio, stateful workloads, GitOps, Operators, and full observability — every module is built for engineers who need to own Kubernetes in production. Mapped directly to the CKA and CKS certification paths and to what architects and SREs deal with on the job every day.",
    whatYouLearn: [
      "Design and bootstrap a production Kubernetes cluster from scratch — control plane, etcd, and worker nodes",
      "Understand the internal operation of the API Server — authentication, authorisation, and admission controllers",
      "Manage users, service accounts, and fine-grained access control with RBAC",
      "Enforce resource fairness across teams using LimitRange, ResourceQuota, and PriorityClasses",
      "Secure workloads with SecurityContext, PodSecurity standards, Kyverno, OPA/Gatekeeper, and Falco",
      "Design and enforce network isolation using L3/L4 and L7 Network Policies with Cilium",
      "Configure production-grade Ingress with NGINX and the Gateway API",
      "Manage persistent storage in production — CSI, OpenEBS, Longhorn, and cloud-native volumes",
      "Monitor clusters end-to-end with the Prometheus Operator and Grafana",
      "Deploy and manage applications with Helm, Kustomize, and GitOps via ArgoCD",
      "Extend Kubernetes with Custom Resource Definitions and the Operator Framework",
      "Implement a full service mesh with Istio and Linkerd for traffic management, tracing, and mTLS",
      "Prepare for and pass the CKA and CKS certification exams",
    ],
    curriculum: [
      {
        module: 1,
        title: "Kubernetes Architecture Fundamentals",
        lessons: [
          "The Big Picture",
          "The Control Plane, Piece by Piece",
          "The Node Side (Data Plane)",
          "Networking & Discovery",
          "Putting It Together",
          "Configuration Basics",
        ],
      },
      {
        module: 2,
        title: "Cluster Installation and Production Administration",
        lessons: [
          "Bootstrapping etcd & the Control Plane",
          "Bootstrapping Worker Nodes",
          "Deployment Tools",
          "High Availability Clusters",
          "Production Security & Maintenance",
          "Upgrades & Operations",
          "kubectl Deep Dive",
        ],
      },
      {
        module: 3,
        title: "Access Management with RBAC",
        lessons: [
          "RBAC Fundamentals",
          "Permission Structure",
          "Users & Service Accounts",
          "Testing & Auditing Access",
          "RBAC Best Practices",
        ],
      },
      {
        module: 4,
        title: "Resource Management",
        lessons: [
          "Namespaces",
          "LimitRange & ResourceQuota",
          "QoS Classes",
          "PriorityClass",
          "Practical Exercise",
        ],
      },
      {
        module: 5,
        title: "Kubernetes Security",
        lessons: [
          "Pod Security",
          "Admission Controllers",
          "Policy as Code",
          "Runtime & Image Security",
          "Network Policies",
        ],
      },
      {
        module: 6,
        title: "Ingress, Gateway API, and HTTP Access Control",
        lessons: [
          "Ingress Fundamentals",
          "NGINX Ingress Controller",
          "TLS Termination",
          "Gateway API",
          "MetalLB",
        ],
      },
      {
        module: 7,
        title: "Scheduling Optimization",
        lessons: [
          "Node-Based Scheduling",
          "Pod-Based Scheduling",
          "Taints & Tolerations",
          "Topology Spread Constraints",
          "Pod Disruption Budgets",
        ],
      },
      {
        module: 8,
        title: "Storage Management in Production",
        lessons: [
          "The CSI Model",
          "StorageClasses",
          "Persistent Volumes & PVCs",
          "Cloud Storage Integration",
          "On-Prem Storage",
          "StatefulSets & Storage",
        ],
      },
      {
        module: 9,
        title: "Monitoring and Observability",
        lessons: [
          "Observability Goals",
          "Prometheus Operator",
          "PromQL",
          "Grafana",
          "Log Management",
          "Kubernetes-Specific Metrics",
          "Scaling Prometheus",
        ],
      },
      {
        module: 10,
        title: "Helm, Kustomize, and GitOps",
        lessons: [
          "Helm Architecture",
          "Helm Commands",
          "Custom Helm Charts",
          "Kustomize",
          "Helm vs Kustomize",
          "GitOps Principles",
          "ArgoCD vs Flux",
          "ArgoCD in Practice",
          "CI/CD + GitOps",
        ],
      },
      {
        module: 11,
        title: "Operators and Extending Kubernetes",
        lessons: [
          "Extension Methods",
          "Custom Resource Definitions",
          "The Operator Pattern",
          "Building Operators",
          "Real-World Operators",
          "Helm 2 vs Helm 3",
        ],
      },
      {
        module: 12,
        title: "Service Mesh — Istio and Linkerd",
        lessons: [
          "Service Mesh Fundamentals",
          "Istio Architecture",
          "Installing Istio",
          "Traffic Management",
          "Observability",
          "Istio Gateway",
          "Istio mTLS",
          "Linkerd2",
          "Choosing Between Istio and Linkerd",
        ],
      },
    ],
    whoIsThisFor: [
      "DevOps engineers and SREs who manage Kubernetes in production",
      "Infrastructure architects designing scalable container platforms",
      "System administrators moving into Kubernetes operations",
      "Security professionals responsible for container and cluster security",
      "Anyone preparing for the CKA or CKS certification exams",
    ],
    tools: ["Kubernetes", "Helm", "ArgoCD", "Istio", "Linkerd", "Prometheus", "Grafana", "Kyverno", "OPA", "Falco", "Trivy", "Cilium", "Terraform"],
    instructor: {
      name: "Sudhan K S",
      title: "Senior Cloud Architect & DevOps Engineer",
      experience: "18 years",
      certifications: [
        "AWS Certified Solutions Architect – Professional",
        "Google Cloud Certified Solutions Architect",
        "RHCE",
        "MCSA",
      ],
      bio: "Founder of DeOrigin Labs and Dorigin Technologies. Has trained professionals at Cognizant, Mphasis, California Institute of Technology, and Don Bosco Institute of Technology. Brings real project experience to every session.",
    },
    highlights: [
      "Best-in-market fees",
      "100% job oriented curriculum",
      "Designed with 18 years industry experience",
      "Career opportunities in India & abroad",
      "500+ students trained",
      "5 intakes per month",
    ],
  },
};

const CATEGORY_COLORS = {
  Networking: "bg-blue-100 text-blue-700",
  Cloud: "bg-emerald-100 text-emerald-700",
  DevOps: "bg-orange-100 text-orange-700",
};

const TESTIMONIALS = {
  "linux-and-networking-essentials": [
    { name: "Rahul Sharma", role: "Junior DevOps Engineer, Bangalore", text: "Coming from zero Linux knowledge, this course gave me the confidence to work on production servers within weeks of completing it." },
    { name: "Priya Nair", role: "Cloud Support Engineer, Mysore", text: "The shell scripting module alone was worth the fee. I use those scripts at work every single day." },
    { name: "Arun Kumar", role: "IT Student, Don Bosco Institute", text: "Sudhan sir explains every command with a real-world use case. Never felt lost even once." },
  ],
  "cloud-essentials": [
    { name: "Kiran Patel", role: "Cloud Intern, TCS", text: "I tried learning cloud from YouTube for months. One month with DeOrigin Labs and I landed my first cloud internship." },
    { name: "Lakshmi Devi", role: "B.Tech Student, Mysore", text: "The fact that we touched AWS, GCP and Azure in one course is incredible value. No other institute offers this at this price." },
    { name: "Suresh Babu", role: "Junior Cloud Engineer, Wipro", text: "Hands-on labs from day one. By week two I was already deploying VMs on all three platforms." },
  ],
  "aws-professional-architect": [
    { name: "Vikram Menon", role: "AWS Solutions Architect, Accenture", text: "Cleared SAP-C02 on first attempt. The Well-Architected Framework sessions were exactly what the exam needed." },
    { name: "Ananya Krishnan", role: "Cloud Architect, Deloitte", text: "Sudhan sir has real enterprise experience and it shows. Every architecture pattern came with a war story from production." },
    { name: "Rohit Joshi", role: "Senior DevOps Engineer, Bangalore", text: "The cost optimization module saved my company lakhs within a month of me applying what I learned here." },
  ],
  "gcp-professional-architect": [
    { name: "Naveen Gowda", role: "GCP Data Engineer, Google Partner Firm", text: "BigQuery and Dataflow sessions were world class. Better than any online course I have paid 10x more for." },
    { name: "Divya Shetty", role: "Cloud Engineer, Mysore", text: "Passed the GCP Professional Cloud Architect exam with a strong score. The case study approach in week 6 was the key." },
    { name: "Rajesh Iyer", role: "Platform Engineer, Bangalore", text: "Sudhan sir is one of the few trainers who has actually built GCP infrastructure for real clients. That experience is priceless." },
  ],
  "azure-architect": [
    { name: "Pooja Reddy", role: "Azure Cloud Engineer, Microsoft Partner", text: "AZ-305 cleared in the first attempt. The governance and policy sessions were the most thorough I have seen anywhere." },
    { name: "Santhosh Kumar", role: "Infrastructure Architect, HCL", text: "The Azure Databricks and Unity Catalog content is something even senior engineers struggle with. Explained perfectly here." },
    { name: "Meera Pillai", role: "Cloud Consultant, Cognizant", text: "I attended Sudhan sir's corporate training at Cognizant and came back for this course. The quality is consistently excellent." },
  ],
  "devops-essentials": [
    { name: "Aditya Rao", role: "DevOps Trainee, Mphasis", text: "I went from not knowing what Docker was to deploying containerized apps in 4 weeks. The pace is perfect." },
    { name: "Bhavana Hegde", role: "Software Developer, Startup Bangalore", text: "As a developer who never touched infrastructure, this course made CI/CD feel completely natural." },
    { name: "Chetan Naik", role: "IT Graduate, Mysore", text: "Got placed as a junior DevOps engineer within 6 weeks of completing this course. The job-oriented approach works." },
  ],
  "kubernetes-architect": [
    { name: "Aditya Rao", role: "DevOps Trainee, Mphasis", text: "I went from not knowing what Docker was to deploying containerized apps in 4 weeks. The pace is perfect." },
    { name: "Bhavana Hegde", role: "Software Developer, Startup Bangalore", text: "As a developer who never touched infrastructure, this course made CI/CD feel completely natural." },
    { name: "Chetan Naik", role: "IT Graduate, Mysore", text: "Got placed as a junior DevOps engineer within 6 weeks of completing this course. The job-oriented approach works." },
  ],
};

const ACCENT_COLORS = {
  Foundation: "bg-blue-500",
  Cloud: "bg-emerald-500",
  DevOps: "bg-orange-500",
};

const TestimonialStrip = ({ items }) => {
  const stripRef = useRef(null)

  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
      }}
    >
      <div
        ref={stripRef}
        className="flex gap-6"
        style={{ animation: 'scroll-left 18s linear infinite' }}
        onMouseEnter={() => { if (stripRef.current) stripRef.current.style.animationPlayState = 'paused' }}
        onMouseLeave={() => { if (stripRef.current) stripRef.current.style.animationPlayState = 'running' }}
      >
        {[...items.map((t, i) => ({ t, key: `a-${i}` })), ...items.map((t, i) => ({ t, key: `b-${i}` })), ...items.map((t, i) => ({ t, key: `c-${i}` }))].map(({ t, key }) => (
          <div key={key} className="min-w-[300px] bg-white rounded-2xl p-6 shadow-md flex flex-col gap-3">
            <span className="text-4xl text-green-500 font-serif leading-none">"</span>
            <p className="text-gray-700 text-sm leading-relaxed">{t.text}</p>
            <div>
              <div className="font-semibold text-gray-900">{t.name}</div>
              <div className="text-xs text-gray-500">{t.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.531 5.858L.044 23.338a.75.75 0 00.918.918l5.478-1.488A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.726 9.726 0 01-4.964-1.355l-.355-.212-3.702 1.005 1.005-3.702-.212-.355A9.726 9.726 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z" />
  </svg>
)

const CourseDetail = () => {
  const { slug } = useParams()
  const course = COURSES_DATA[slug]
  const [openItem, setOpenItem] = useState(0)

  if (!course) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500 text-lg">Course not found.</p>
    </div>
  )

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in the ${course.title} course`

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">

      {/* Hero Section */}
      <section className="bg-white border-b border-gray-100 mt-4">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-4">

          {/* Breadcrumb */}
          <div className="text-sm text-gray-500 mb-4">
            <Link to="/courses" className="hover:text-gray-700 transition-colors">Courses</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-900">{course.title}</span>
          </div>

          {/* Category pill + level pill */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${CATEGORY_COLORS[course.category]}`}>
              {course.category}
            </span>
            {course.level && (
              <span className="inline-block text-xs font-semibold rounded-full px-3 py-1 bg-blue-100 text-blue-700">
                {course.level}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{course.title}</h1>

          {/* Pitch */}
          <p className="text-lg text-gray-600 mb-6 max-w-2xl">{course.pitch}</p>

          <div className="flex items-center gap-2 text-gray-500 text-base mt-4">
            <Clock className="w-4 h-4" />
            <span>Duration: {course.duration}</span>
          </div>



        </div>
      </section>

      {/* Two-column layout */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8 space-y-10 lg:space-y-0">

          {/* Left column */}
          <div className="lg:col-span-2 space-y-10">

            {/* Overview */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">About This Course</h2>
              <p className="text-gray-600 leading-relaxed">{course.overview}</p>
            </div>

            {/* What You'll Learn */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What You'll Learn</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {course.whatYouLearn.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Curriculum */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Curriculum</h2>
              <div className="space-y-2">
                {course.curriculum.map((item, i) => (
                  <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenItem(openItem === i ? -1 : i)}
                      className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 text-left transition-colors"
                    >
                      <span className="font-medium text-gray-900">
                        Module {item.module} — {item.title}
                      </span>
                      {openItem === i
                        ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      }
                    </button>
                    {openItem === i && (
                      <div className="px-5 pb-4 pt-1 bg-gray-50 text-sm text-gray-600 leading-relaxed">
                        {item.lessons ? (
                          <>
                            {item.description && (
                              <p className="mb-3 text-gray-500 italic">{item.description}</p>
                            )}
                            <ol className="list-none space-y-1">
                              {item.lessons.map((lesson, j) => (
                                <li key={j} className="flex gap-2">
                                  <span className="text-gray-400 font-mono w-5 shrink-0">{j + 1}.</span>
                                  <span>{lesson}</span>
                                </li>
                              ))}
                            </ol>
                          </>
                        ) : (
                          item.topics
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Who Is This For */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Who Is This For?</h2>
              <ul className="space-y-2">
                {course.whoIsThisFor.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-green-500 font-bold mt-0.5">→</span>
                    {item}
                  </li>
                ))}
              </ul>
              {course.prerequisite && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                  <div className="text-sm font-semibold text-amber-900 mb-1">Prerequisite</div>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {course.prerequisite.text}
                    {course.prerequisite.slug && (
                      <>
                        {' '}
                        <Link
                          to={`/courses/${course.prerequisite.slug}`}
                          className="font-semibold underline underline-offset-2 hover:text-amber-900"
                        >
                          {course.prerequisite.title}
                        </Link>
                        {' '}first.
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Instructor */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Instructor</h2>
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-start gap-5">
                  <img
                    src="/instructor-sudhan.jpg"
                    alt={`${course.instructor.name}, ${course.instructor.title}`}
                    width="80"
                    height="80"
                    loading="lazy"
                    decoding="async"
                    className="w-20 h-20 rounded-full object-cover object-center bg-gray-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-bold text-gray-900">{course.instructor.name}</div>
                    <div className="text-sm text-gray-500 mb-1">{course.instructor.title}</div>
                    <div className="text-sm text-gray-500 mb-3">{course.instructor.experience} experience</div>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">{course.instructor.bio}</p>
                    <div className="flex flex-wrap gap-2">
                      {course.instructor.certifications.map((cert, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 rounded-full px-3 py-1">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right column — sticky sidebar */}
          <div className="block lg:sticky lg:top-24 space-y-6">
            <div className="sticky top-24 space-y-6">

              {/* Enroll card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="mb-2">
                  <span className="text-sm text-gray-500">Program Fee: </span>
                  <span className="text-xl font-semibold text-gray-800">₹{course.price.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-4">
                  <Clock className="w-4 h-4" />
                  {course.duration}
                </div>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-3 rounded-xl transition-colors mb-4"
                >
                  <WhatsAppIcon />
                  Enquire on WhatsApp
                </a>
                <p className="text-xs text-gray-400 text-center">5 intakes per month — next batch starting soon</p>
                <p className="text-xs text-gray-400 text-center mt-1">Online</p>
              </div>

              {/* Highlights card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Why DeOrigin Labs?</h3>
                <ul className="space-y-2">
                  {course.highlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tools card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Tools & Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {course.tools.map((tool, i) => (
                    <span key={i} className="bg-gray-100 text-gray-700 rounded px-2 py-1 text-sm">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Testimonials */}
      {TESTIMONIALS[slug] && (
        <section className="mt-12">
          <style>{`
            @keyframes scroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(-33.333%); }
            }
          `}</style>
          <div className="max-w-6xl mx-auto px-4">
            <div className="bg-green-700 rounded-2xl py-12 overflow-hidden">
              <div className="mb-6 px-8">
                <h2 className="text-white text-2xl font-bold">What Our Students Say</h2>
                <p className="text-green-200 text-sm mt-1">Real outcomes from real learners</p>
              </div>
              <TestimonialStrip items={TESTIMONIALS[slug]} />
            </div>
          </div>
        </section>
      )}


    </div>
  )
}

export default CourseDetail
